import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CandidateExperience {
  id: string;
  candidate_id: string;
  role: string;
  company: string;
  industry?: string;
  start_date: string | null;
  end_date: string | null;
  skills?: string[];
  metadata?: Record<string, any>;
  is_current?: boolean;
}

export interface CandidateSkill {
  name: string;
  context?: string;
}

export const useCandidateExperiences = (candidateId: string | undefined) => {
  const [loading, setLoading] = useState(true);
  const [experiences, setExperiences] = useState<CandidateExperience[]>([]);
  const [skills, setSkills] = useState<CandidateSkill[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const fetchExperiences = useCallback(async () => {
    if (!candidateId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch candidate experiences with proper date fields
      const { data, error: fetchError } = await supabase
        .from('candidate_experiences')
        .select(`
          id,
          candidate_id,
          raw_text,
          start_date,
          end_date,
          metadata
        `)
        .eq('candidate_id', candidateId)
        .not('start_date', 'is', null) // Only get experiences with dates
        .order('end_date', { ascending: false, nullsFirst: true }) // Current positions first (null end_date)
        .order('start_date', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      // Fetch ALL candidate skills separately
      const { data: skillsData } = await supabase
        .from('candidate_skills')
        .select('raw_skill_name, context')
        .eq('candidate_id', candidateId);

      // Store skills separately
      const candidateSkills: CandidateSkill[] = (skillsData || [])
        .map(s => ({
          name: s.raw_skill_name,
          context: s.context || undefined
        }))
        .filter(s => s.name) as CandidateSkill[];

      setSkills(candidateSkills);

      // Transform experiences - don't attach skills to them
      const transformedExperiences: CandidateExperience[] = (data || []).map(exp => {
        const company = exp.metadata?.company || 'Unknown Company';
        const industry = exp.metadata?.industry;
        const role = exp.raw_text || 'Position';
        const is_current = exp.end_date === null;

        return {
          id: exp.id,
          candidate_id: exp.candidate_id,
          role,
          company,
          industry,
          start_date: exp.start_date,
          end_date: exp.end_date,
          skills: [], // Skills are calculated separately
          metadata: exp.metadata,
          is_current,
        };
      });

      setExperiences(transformedExperiences);
    } catch (err) {
      console.error('Error fetching candidate experiences:', err);
      setError(err as Error);
      toast.error('Failed to load candidate experiences');
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    fetchExperiences();
  }, [fetchExperiences]);

  return {
    loading,
    experiences,
    skills,
    error,
    refetch: fetchExperiences,
  };
};

